'use client';
import React, { useState, useEffect, useCallback } from 'react';
import NotificationToast from './NotificationToast';
import { useNotifications, Notification } from '@/context/NotificationsContext';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/navigation';

interface ToastNotification {
  id: string;
  title: string;
  message: string;
  type?: 'follow_request_accepted' | 'flight_verified' | 'follow_request' | 'like' | 'new_follower' | 'flight_marked_for_review';
  timestamp: number;
  actionUrl?: string;
  originalNotification: Notification;
}

const NotificationToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const { onNewNotification, offNewNotification, markAsRead, openNotifications } = useNotifications();
  const { t } = useTranslation();
  const router = useRouter();


  const handleNewNotification = useCallback((notification: Notification) => {
    
    // Only show toast for unread notifications
    if (notification.read_at) {
      return;
    }

    const params = { ...notification.data.message_params };
    
    if (params.source === 'system') {
      params.source = t('notifications.source.system');
    }

    const toast: ToastNotification = {
      id: notification.id,
      title: notification.data.title || String(t(`notifications.${notification.data.notification_title}`, params)),
      message: String(t(`notifications.${notification.data.message_key}`, params)),
      type: notification.data.type as 'follow_request_accepted' | 'flight_verified' | 'follow_request' | 'like' | 'new_follower' | 'flight_marked_for_review',
      timestamp: Date.now(),
      actionUrl: notification.data.action_url,
      originalNotification: notification
    };


    setToasts(prev => {
      const newToasts = [toast, ...prev].slice(0, 3);
      return newToasts;
    });
  }, [t]);

  useEffect(() => {
    
    // Subscribe to new notifications
    onNewNotification(handleNewNotification);

    // Cleanup subscription
    return () => {
      offNewNotification(handleNewNotification);
    };
  }, [onNewNotification, offNewNotification, handleNewNotification]);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleToastClick = (toast: ToastNotification) => {
    
    // Mark as read
    markAsRead(toast.id);
    
    // Handle action URL
    if (toast.actionUrl) {
      router.push(toast.actionUrl);
    } else {
      // If no action URL, just open notifications drawer
      openNotifications();
    }
  };

  return (
      <div className="fixed top-4 right-4 z-[100] space-y-2 pointer-events-none">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className="pointer-events-auto"
            style={{
              transform: `translateY(${index * 8}px)`,
              zIndex: 100 - index,
            }}
          >
            <NotificationToast
              id={toast.id}
              title={toast.title}
              message={toast.message}
              type={toast.type}
              onClose={removeToast}
              onClick={() => handleToastClick(toast)}
              duration={6000} // 6 seconds
            />
          </div>
        ))}
      </div>
  );
};

export default NotificationToastContainer;